import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  getWebhookMappings,
  createWebhookMapping,
  updateWebhookMapping,
  deleteWebhookMapping,
} from '@/services/api/webhookMappingService';
import type { WebhookMapping, WebhookMappingCreate, Product } from '@types';

const API_BASE = import.meta.env.VITE_API_URL || 'https://ninshousystem-api-361153976392.asia-northeast1.run.app';

export const WebhookMappingsPage = () => {
  const [mappings, setMappings] = useState<WebhookMapping[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<WebhookMapping | null>(null);
  const [providerFilter, setProviderFilter] = useState<string>('');

  const [formData, setFormData] = useState<WebhookMappingCreate>({
    provider: 'koneone',
    external_product_id: '',
    product_id: 0,
    plan_code: '',
  });

  // プロダクト一覧を取得
  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_BASE}/products`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error('プロダクト一覧の取得に失敗しました');
      const data = await res.json();
      setProducts(data);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
  };

  // Webhookマッピング一覧を取得
  const fetchMappings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getWebhookMappings(providerFilter || undefined);
      setMappings(response.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchMappings();
  }, [providerFilter]);

  const handleOpenDialog = (mapping?: WebhookMapping) => {
    if (mapping) {
      setEditingMapping(mapping);
      setFormData({
        provider: mapping.provider,
        external_product_id: mapping.external_product_id,
        product_id: mapping.product_id,
        plan_code: mapping.plan_code,
      });
    } else {
      setEditingMapping(null);
      setFormData({
        provider: 'koneone',
        external_product_id: '',
        product_id: products && products.length > 0 ? products[0]?.id ?? 0 : 0,
        plan_code: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMapping(null);
  };

  const handleSave = async () => {
    try {
      if (editingMapping) {
        await updateWebhookMapping(editingMapping.id, formData);
      } else {
        await createWebhookMapping(formData);
      }
      handleCloseDialog();
      fetchMappings();
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存エラー');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('このマッピングを削除しますか？')) return;

    try {
      await deleteWebhookMapping(id);
      fetchMappings();
    } catch (err) {
      setError(err instanceof Error ? err.message : '削除エラー');
    }
  };

  const getProviderLabel = (provider: string): string => {
    const labels: Record<string, string> = {
      koneone: 'Connected One',
      stripe: 'Stripe',
      paypal: 'PayPal',
    };
    return labels[provider] || provider;
  };

  const getProductName = (productId: number): string => {
    const product = products.find((p) => p.id === productId);
    return product ? product.name : `ID: ${productId}`;
  };

  const selectedProduct = products.find((p) => p.id === formData.product_id);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Webhookマッピング管理</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>プロバイダー</InputLabel>
            <Select
              value={providerFilter}
              label="プロバイダー"
              onChange={(e: SelectChangeEvent) => setProviderFilter(e.target.value)}
            >
              <MenuItem value="">すべて</MenuItem>
              <MenuItem value="koneone">Connected One</MenuItem>
              <MenuItem value="stripe">Stripe</MenuItem>
              <MenuItem value="paypal">PayPal</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchMappings}
            disabled={loading}
          >
            更新
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            新規追加
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>プロバイダー</TableCell>
                <TableCell>外部商品ID</TableCell>
                <TableCell>プロダクト</TableCell>
                <TableCell>プランコード</TableCell>
                <TableCell>ステータス</TableCell>
                <TableCell>作成日</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mappings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    マッピングがありません
                  </TableCell>
                </TableRow>
              ) : (
                mappings.map((mapping) => (
                  <TableRow key={mapping.id}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {mapping.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getProviderLabel(mapping.provider)}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {mapping.external_product_id}
                      </Typography>
                    </TableCell>
                    <TableCell>{getProductName(mapping.product_id)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {mapping.plan_code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={mapping.is_active ? '有効' : '無効'}
                        color={mapping.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(mapping.created_at).toLocaleDateString('ja-JP')}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="編集">
                        <IconButton onClick={() => handleOpenDialog(mapping)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="削除">
                        <IconButton onClick={() => handleDelete(mapping.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 追加/編集ダイアログ */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingMapping ? 'マッピング編集' : '新規マッピング追加'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>プロバイダー</InputLabel>
              <Select
                value={formData.provider}
                label="プロバイダー"
                onChange={(e: SelectChangeEvent) =>
                  setFormData({ ...formData, provider: e.target.value })
                }
              >
                <MenuItem value="koneone">Connected One</MenuItem>
                <MenuItem value="stripe">Stripe</MenuItem>
                <MenuItem value="paypal">PayPal</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="外部商品ID"
              value={formData.external_product_id}
              onChange={(e) =>
                setFormData({ ...formData, external_product_id: e.target.value })
              }
              helperText="例: prod_123456789 (Stripe), P-12345 (PayPal)"
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>プロダクト</InputLabel>
              <Select
                value={formData.product_id}
                label="プロダクト"
                onChange={(e: SelectChangeEvent<number>) =>
                  setFormData({ ...formData, product_id: Number(e.target.value) })
                }
              >
                {products.map((product) => (
                  <MenuItem key={product.id} value={product.id}>
                    {product.name} ({product.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>プランコード</InputLabel>
              <Select
                value={formData.plan_code}
                label="プランコード"
                onChange={(e: SelectChangeEvent) =>
                  setFormData({ ...formData, plan_code: e.target.value })
                }
                disabled={!selectedProduct || selectedProduct.plans.length === 0}
              >
                {selectedProduct?.plans.map((plan) => (
                  <MenuItem key={plan.code} value={plan.code}>
                    {plan.name} ({plan.code}) - ¥{plan.price}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedProduct && selectedProduct.plans.length === 0 && (
              <Alert severity="warning">
                選択したプロダクトにはプランがありません
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              !formData.provider ||
              !formData.external_product_id ||
              !formData.product_id ||
              !formData.plan_code
            }
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
